import { createDocumentBridge, type DocumentBridgeBundle } from "@codenexus/react";
import { IntelligenicTipTapAdapter } from "./IntelligenicTipTapAdapter";
import { MonacoEditorAdapter } from "./MonacoEditorAdapter";
import { CompositeHostAdapter } from "./CompositeHostAdapter";
import { getHeadlessSectionEditors } from "./HeadlessSectionEditors";

/**
 * Module-level singletons: one TipTap (prose) adapter, one Monaco (code)
 * adapter, and a composite that routes between them. The bundle is built from
 * the composite so the chat panel serves both editor surfaces from one set of
 * tools.
 *
 * Only one editor surface is mounted at a time (editorSurface is "ide" XOR
 * "rich-text"), so routing collisions cannot occur; the composite merges
 * cleanly either way. TiptapEditor registers via `.adapter`; the code editor
 * feeds `.monacoAdapter` via syncOpenDocs.
 */
const tiptapAdapter = new IntelligenicTipTapAdapter();
tiptapAdapter.attachHeadless(getHeadlessSectionEditors());
const monacoAdapter = new MonacoEditorAdapter();

const compositeAdapter = new CompositeHostAdapter([
    // Code docs first (specific), prose catch-all last.
    { adapter: monacoAdapter, owns: (docId) => monacoAdapter.owns(docId) },
    { adapter: tiptapAdapter, owns: () => true }
]);

const hostDocumentBundle: DocumentBridgeBundle = createDocumentBridge(compositeAdapter);

export interface HostDocumentBridge {
    /** Prose adapter — TiptapEditor.register() targets this directly. */
    adapter: IntelligenicTipTapAdapter;
    /** Code adapter — WorkProductEditor_Code feeds this via syncOpenDocs. */
    monacoAdapter: MonacoEditorAdapter;
    /** The EditorAdapter the bundle is built from; watch this for changes. */
    composite: CompositeHostAdapter;
    bundle: DocumentBridgeBundle;
}

// Referentially stable: TiptapEditor calls this on every render and feeds the
// result into useEffect dependency arrays. A fresh object per call re-fires
// those effects each render — the value-reset effect then loops
// setContent → transaction re-render → setContent until React error #185.
const hostDocumentBridge: HostDocumentBridge = {
    adapter: tiptapAdapter,
    monacoAdapter,
    composite: compositeAdapter,
    bundle: hostDocumentBundle
};

export function getHostDocumentBridge(): HostDocumentBridge {
    return hostDocumentBridge;
}
